import { ISingleOrder } from '@/_service/types/orders';
import { getTracking } from '@/_service/yandex.service';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import YandexMapFrame from '@/_components/Map/shared/mapBridge/YandexMapFrame';
import { useMapBridge, buildMapSrc } from '@/_components/Map/shared/mapBridge/useMapBridge';
import { MAP_MODES, MESSAGE_TYPES } from '@/_components/Map/shared/mapBridge/constants';

type Props = {
  order: ISingleOrder;
};

const DEFAULT_ZOOM = 13;
const DESTINATION_MARKER_ID = 'order-destination';
const COURIER_MARKER_ID = 'order-courier';

const CourierMap: React.FC<Props> = ({ order }) => {
  const center = useMemo(
    () => [order?.to_location?.lat, order?.to_location?.long],
    [order?.to_location?.lat, order?.to_location?.long],
  );

  const { data } = useQuery({
    queryKey: ['couriers', order?.id],
    queryFn: () =>
      getTracking({
        courier_id: order?.courier_id,
      }).then((res) => res.data),
    enabled: Boolean(order?.courier_id),
    refetchInterval: 10000,
  });

  const sendMarkers = () => {
    const markers: {
      id: string;
      coords: number[];
      name?: string;
      address?: string;
      title?: string;
      iconType?: 'car' | 'caption';
    }[] = [];

    if (order?.to_location) {
      markers.push({
        id: DESTINATION_MARKER_ID,
        coords: [order.to_location.lat, order.to_location.long],
        title: order?.to_address || order?.steps?.[0]?.branch_name,
        address: order?.to_address,
        iconType: 'caption',
      });
    }

    if (data?.id && data?.location) {
      markers.push({
        id: COURIER_MARKER_ID,
        coords: [data.location.lat, data.location.long],
        iconType: 'car',
      });
    }

    postToFrame(MESSAGE_TYPES.SET_MARKERS, { markers });
  };

  const { iframeRef, postToFrame } = useMapBridge(({ type }) => {
    if (type === MESSAGE_TYPES.MAP_READY) {
      sendMarkers();
    }
  });

  // Re-sync markers whenever the destination or the polled courier position changes.
  useEffect(() => {
    sendMarkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    order?.to_location?.lat,
    order?.to_location?.long,
    data?.location?.lat,
    data?.location?.long,
  ]);

  const iframeSrc = useMemo(
    () =>
      buildMapSrc({
        lat: center[0],
        lng: center[1],
        zoom: DEFAULT_ZOOM,
        mode: MAP_MODES.SELF_PICKUP,
      }),
    [center],
  );

  return (
    <YandexMapFrame
      ref={iframeRef}
      src={iframeSrc}
      style={{ borderRadius: 12, overflow: 'hidden' }}
    />
  );
};

export default CourierMap;
